import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Layers,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  Eye,
  Columns2,
  FileCode2,
  Palette,
  Copy,
  Sliders,
  Type,
  Table as TableIcon,
  Minus,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Pencil,
  X,
  Sparkles,
  Search,
  Upload,
  Loader2,
  Check,
  AlertCircle,
  Sun,
  Moon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  htmlTemplatesApi,
  sanitizeTemplateConfig,
  type SourceMetadata,
  type HtmlTemplateConfig,
  type TextWidget,
  type TableWidget,
  type DividerWidget,
  type TableColumn,
} from '../../services/htmlTemplatesApi';
import TiptapRichTextEditor from './components/templates/TiptapRichTextEditor';
import HtmlEmailPreview from './components/templates/HtmlEmailPreview';
import { CustomSelect, type SelectOption } from './components/CustomSelect';
import { useClientLevel } from '../../hooks/useClientLevel';
import { getClientId } from '../../services/automationapi';

const ALIGN_OPTIONS: SelectOption[] = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const LOGO_POSITION_OPTIONS: SelectOption[] = [
  { label: 'Inline Left (Floating)', value: 'inline-left' },
  { label: 'Inline Right (Floating)', value: 'inline-right' },
  { label: 'Top Left', value: 'top-left' },
  { label: 'Top Center', value: 'top-center' },
  { label: 'Top Right', value: 'top-right' },
];

const LOGO_WIDTH_OPTIONS: SelectOption[] = [
  { label: '32px (Compact)', value: '32px' },
  { label: '40px (Small)', value: '40px' },
  { label: '48px (Medium)', value: '48px' },
  { label: '56px (Standard)', value: '56px' },
  { label: '64px (Large)', value: '64px' },
  { label: '80px (X-Large)', value: '80px' },
  { label: '96px (2X-Large)', value: '96px' },
  { label: '120px (Default)', value: '120px' },
  { label: '140px (Expanded)', value: '140px' },
  { label: '160px (Hero)', value: '160px' },
  { label: '200px (Banner)', value: '200px' },
];

const FONT_SIZE_OPTIONS: SelectOption[] = [
  { label: '14px (Small)', value: '14px' },
  { label: '16px (Normal)', value: '16px' },
  { label: '18px (Medium)', value: '18px' },
  { label: '20px (Large - Default)', value: '20px' },
  { label: '22px (X-Large)', value: '22px' },
  { label: '24px (2X-Large)', value: '24px' },
  { label: '28px (Title)', value: '28px' },
  { label: '32px (Heading)', value: '32px' },
  { label: '36px (Hero)', value: '36px' },
];

const FONT_WEIGHT_OPTIONS: SelectOption[] = [
  { label: '400 (Regular)', value: '400' },
  { label: '500 (Medium)', value: '500' },
  { label: '600 (Semi-Bold)', value: '600' },
  { label: '700 (Bold - Default)', value: '700' },
  { label: '800 (Extra Bold)', value: '800' },
  { label: '900 (Black)', value: '900' },
];

const MAX_WIDTH_OPTIONS: SelectOption[] = [
  { label: '560px (Narrow)', value: '560px' },
  { label: '600px (Standard Email)', value: '600px' },
  { label: '640px (Default)', value: '640px' },
  { label: '680px (Wide)', value: '680px' },
  { label: '720px (Extra Wide)', value: '720px' },
  { label: '800px (Expanded)', value: '800px' },
  { label: '100% (Full Width)', value: '100%' },
];

const FONT_FAMILY_OPTIONS: SelectOption[] = [
  { label: 'System Sans (Default)', value: 'system-ui, -apple-system, sans-serif' },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica Neue', value: '"Helvetica Neue", Helvetica, sans-serif' },
  { label: 'Segoe UI', value: '"Segoe UI", Tahoma, Geneva, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'JetBrains Mono', value: '"JetBrains Mono", monospace' },
];

const DEFAULT_TEMPLATE_CONFIG: HtmlTemplateConfig = {
  header: '',
  header_align: 'left',
  header_font_size: '20px',
  header_font_weight: 'bold',
  header_text_color: '#ef4444',
  header_bg_color: '',
  logo_url: '',
  logo_position: 'inline-left',
  logo_width: '120px',
  widgets: [],
  footer: '',
  primary_color: '#ef4444',
  background_color: '#f8fafc',
  card_background: '#ffffff',
  text_color: '#111827',
  muted_color: '#64748b',
  border_color: '#e2e8f0',
  max_width: '640px',
  font_family: 'system-ui, -apple-system, sans-serif',
};

const DEFAULT_CLOSE_CONFIG: HtmlTemplateConfig = {
  ...DEFAULT_TEMPLATE_CONFIG,
  header: '',
  header_text_color: '#10b981',
  primary_color: '#10b981',
  widgets: [],
  footer: '',
};

const DRAFT_META_KEY = 'html_template_draft_meta';

const getInitialDraftMeta = (routeState?: { name?: string; description?: string; source?: string }) => {
  if (routeState?.name || routeState?.source) {
    return {
      name: routeState.name || '',
      description: routeState.description || '',
      source: routeState.source || '',
    };
  }
  try {
    const saved = sessionStorage.getItem(DRAFT_META_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        name: parsed.name || '',
        description: parsed.description || '',
        source: parsed.source || '',
      };
    }
  } catch {}
  return { name: '', description: '', source: '' };
};

export const HtmlTemplateEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as { name?: string; description?: string; source?: string } | undefined;
  const isEditing = Boolean(id && id !== 'new');

  const currentLevel = useClientLevel();
  const initialClientKeyRef = useRef<string>(
    currentLevel ? `${currentLevel.name}_${currentLevel.id || currentLevel.client_id || ''}` : 'global'
  );

  useEffect(() => {
    const handleLevelChange = () => {
      navigate('/automation?tab=templates');
    };
    window.addEventListener('client-level-changed', handleLevelChange);
    return () => window.removeEventListener('client-level-changed', handleLevelChange);
  }, [navigate]);

  useEffect(() => {
    const currentKey = currentLevel ? `${currentLevel.name}_${currentLevel.id || currentLevel.client_id || ''}` : 'global';
    if (initialClientKeyRef.current !== currentKey) {
      navigate('/automation?tab=templates');
    }
  }, [currentLevel, navigate]);

  const isGlobalLevel = useMemo(() => {
    return !currentLevel || currentLevel.name === 'global' || (!currentLevel.id && !currentLevel.client_id);
  }, [currentLevel]);

  const activeClientId = useMemo(() => {
    if (isGlobalLevel) return undefined;
    const raw = currentLevel?.id !== undefined ? currentLevel.id : currentLevel?.client_id;
    const num = Number(raw);
    return !isNaN(num) ? num : getClientId();
  }, [currentLevel, isGlobalLevel]);

  const initialDraft = useMemo(() => getInitialDraftMeta(routeState), []);

  // Loading & State
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [sources, setSources] = useState<SourceMetadata[]>([]);
  const [selectedSourceMeta, setSelectedSourceMeta] = useState<SourceMetadata | null>(null);

  // Template Metadata
  const [templateName, setTemplateName] = useState<string>(initialDraft.name);
  const [templateDescription, setTemplateDescription] = useState<string>(initialDraft.description);
  const [selectedSource, setSelectedSource] = useState<string>(initialDraft.source || 'applications');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isGlobal, setIsGlobal] = useState<boolean>(isGlobalLevel);

  const isReadOnly = useMemo(() => {
    return Boolean(isEditing && isGlobal && !isGlobalLevel);
  }, [isEditing, isGlobal, isGlobalLevel]);

  // Sync draft metadata to sessionStorage on new templates so refreshing doesn't lose data
  useEffect(() => {
    if (!isEditing && (templateName || templateDescription || selectedSource)) {
      try {
        sessionStorage.setItem(
          DRAFT_META_KEY,
          JSON.stringify({
            name: templateName,
            description: templateDescription,
            source: selectedSource,
          })
        );
      } catch {}
    }
  }, [isEditing, templateName, templateDescription, selectedSource]);

  // Edit Name & Description Modal State
  const [isEditMetaModalOpen, setIsEditMetaModalOpen] = useState<boolean>(false);
  const [editModalName, setEditModalName] = useState<string>('');
  const [editModalDesc, setEditModalDesc] = useState<string>('');

  const handleOpenEditMetaModal = () => {
    setEditModalName(templateName);
    setEditModalDesc(templateDescription);
    setIsEditMetaModalOpen(true);
  };

  const handleSaveMetaModal = () => {
    if (isReadOnly) {
      toast.error('Global templates cannot be edited at client level');
      return;
    }
    if (!editModalName.trim()) {
      toast.error('Template name is required');
      return;
    }
    setTemplateName(editModalName.trim());
    setTemplateDescription(editModalDesc.trim());
    setIsEditMetaModalOpen(false);
    toast.success('Template details updated');
  };

  // Dual Tabs: 'open' vs 'close'
  const [activeTab, setActiveTab] = useState<'open' | 'close'>('open');
  const [openConfig, setOpenConfig] = useState<HtmlTemplateConfig>(DEFAULT_TEMPLATE_CONFIG);
  const [closeConfig, setCloseConfig] = useState<HtmlTemplateConfig>(DEFAULT_CLOSE_CONFIG);

  // View Mode: 'split' | 'editor' | 'preview'
  const [viewMode, setViewMode] = useState<'split' | 'editor' | 'preview'>('split');

  // Canva-styled Sidebar Rail tab: 'widgets' | 'theme'
  const [sidebarTab, setSidebarTab] = useState<'widgets' | 'theme'>('widgets');

  // Collapsible section states
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState<boolean>(false);
  const [isFooterCollapsed, setIsFooterCollapsed] = useState<boolean>(false);
  const [collapsedWidgets, setCollapsedWidgets] = useState<Record<string, boolean>>({});

  const toggleWidgetCollapse = (widgetId: string) => {
    setCollapsedWidgets((prev) => ({ ...prev, [widgetId]: !prev[widgetId] }));
  };

  // Currently active config according to activeTab
  const currentConfig = activeTab === 'open' ? openConfig : closeConfig;
  const setCurrentConfig = (updater: (prev: HtmlTemplateConfig) => HtmlTemplateConfig) => {
    if (isReadOnly) return;
    if (activeTab === 'open') {
      setOpenConfig((prev) => updater(prev));
    } else {
      setCloseConfig((prev) => updater(prev));
    }
  };

  // Logo Upload State & Handlers
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState<boolean>(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_IMAGE_TYPES = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/svg+xml',
    'image/webp',
    'image/gif',
    'image/x-icon',
    'image/vnd.microsoft.icon',
  ];
  const MAX_IMAGE_SIZE_BYTES = 15 * 1024 * 1024; // 15MB

  const handleLogoFileUpload = async (file: File) => {
    const isAllowedType =
      ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase()) ||
      /\.(png|jpe?g|svg|webp|gif|ico)$/i.test(file.name);

    if (!isAllowedType) {
      toast.error('Invalid file format. Please upload a PNG, JPEG, SVG, WEBP, GIF, or ICO image.');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error('Image size exceeds 15MB limit.');
      return;
    }

    setIsUploadingLogo(true);
    const toastId = toast.loading('Uploading logo to public storage...');
    try {
      const res = await htmlTemplatesApi.uploadImage(file);
      const uploadedUrl = res.url || res.data?.url;
      if (uploadedUrl) {
        setCurrentConfig((prev) => ({
          ...prev,
          logo_url: uploadedUrl,
        }));
        toast.success(res.message || 'Logo uploaded successfully', { id: toastId });
      } else {
        toast.error('Upload completed, but no image URL was returned.', { id: toastId });
      }
    } catch (err: any) {
      console.error('Failed to upload logo:', err);
      toast.error(
        err.response?.data?.message || err.message || 'Failed to upload logo',
        { id: toastId }
      );
    } finally {
      setIsUploadingLogo(false);
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = '';
      }
    }
  };

  const handleLogoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  };

  const handleLogoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleLogoFileUpload(e.dataTransfer.files[0]);
    }
  };

  // Fetch available sources and template data on mount
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const sourcesRes = await htmlTemplatesApi.getSources();
        const sourcesList = sourcesRes.data || [];
        setSources(sourcesList);

        if (isEditing && id) {
          const tplRes = await htmlTemplatesApi.getTemplateById(id);
          const tpl = (tplRes as any)?.data?.id ? (tplRes as any).data : ((tplRes as any)?.id ? tplRes : (tplRes as any)?.data);
          if (tpl) {
            setTemplateName(tpl.name || '');
            setTemplateDescription(tpl.description || '');
            setSelectedSource(tpl.source || (sourcesList[0]?.source ?? 'applications'));
            setIsActive(tpl.is_active !== undefined ? tpl.is_active : true);
            setIsGlobal(Boolean(tpl.is_global));
            if (tpl.open_template) {
              setOpenConfig(sanitizeTemplateConfig(tpl.open_template, DEFAULT_TEMPLATE_CONFIG));
            }
            if (tpl.close_template) {
              setCloseConfig(sanitizeTemplateConfig(tpl.close_template, DEFAULT_CLOSE_CONFIG));
            }
          }
        } else {
          // If creating new template, restore source from draft or routeState
          const draft = getInitialDraftMeta(routeState);
          const sourceToUse = routeState?.source || draft.source || selectedSource || (sourcesList.length > 0 ? sourcesList[0].source : 'applications');
          setSelectedSource(sourceToUse);
          if (!templateName && (routeState?.name || draft.name)) {
            setTemplateName(routeState?.name || draft.name || '');
          }
          if (!templateDescription && (routeState?.description || draft.description)) {
            setTemplateDescription(routeState?.description || draft.description || '');
          }
          setIsGlobal(isGlobalLevel);
        }
      } catch (err: any) {
        console.error('Failed to load template or sources:', err);
        toast.error('Failed to load template details');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [id, isEditing]);

  // When selectedSource changes, fetch detailed metadata if not already rich
  useEffect(() => {
    const fetchSourceDetails = async () => {
      if (!selectedSource) return;
      try {
        const details = await htmlTemplatesApi.getSourceDetails(selectedSource);
        setSelectedSourceMeta(details);
      } catch (err) {
        // Fallback to finding in sources array
        const found = sources.find((s) => s.source === selectedSource);
        if (found) {
          setSelectedSourceMeta(found);
        }
      }
    };
    fetchSourceDetails();
  }, [selectedSource, sources]);

  // Helper to safely parse and extract template variables whether passed as an object, array, or string
  const extractVars = (raw: any): Array<{ key: string; category: string }> => {
    if (!raw) return [];
    let parsed = raw;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return [{ key: parsed, category: 'General' }];
      }
    }
    const result: Array<{ key: string; category: string }> = [];
    const seen = new Set<string>();

    const pushVar = (key: string, category: string) => {
      if (!key || typeof key !== 'string') return;
      const cleanKey = key.trim();
      if (!cleanKey || seen.has(cleanKey)) return;
      seen.add(cleanKey);
      result.push({
        key: cleanKey,
        category: category || 'General',
      });
    };

    if (Array.isArray(parsed)) {
      parsed.forEach((v) => {
        if (typeof v === 'string') {
          pushVar(v, 'General');
        } else if (v && typeof v === 'object' && v.key) {
          pushVar(v.key, v.category || 'General');
        }
      });
    } else if (typeof parsed === 'object' && parsed !== null) {
      for (const [catName, varsList] of Object.entries(parsed)) {
        if (Array.isArray(varsList)) {
          varsList.forEach((v) => {
            if (typeof v === 'string') {
              pushVar(v, catName);
            } else if (v && typeof v === 'object' && v.key) {
              pushVar(v.key, catName);
            }
          });
        } else if (typeof varsList === 'string') {
          pushVar(varsList, catName);
        }
      }
    }
    return result;
  };

  // All structured source key items with category grouping from API
  const allSourceKeyItems = useMemo(() => {
    const items: Array<{ key: string; label?: string; category: string }> = [];
    const seen = new Set<string>();

    const add = (key: string, label?: string, category: string = 'General') => {
      if (!key || seen.has(key)) return;
      seen.add(key);
      items.push({ key, label: label || key, category });
    };

    if (selectedSourceMeta) {
      // 1. Template variables from API (grouped by category)
      const vars = [
        ...extractVars(selectedSourceMeta.template_variables),
        ...extractVars(selectedSourceMeta.all_template_variables),
        ...extractVars(selectedSourceMeta.default_template_variables),
      ];
      vars.forEach((v) => {
        add(v.key, undefined, v.category);
      });

      // 2. Only fallback to fields if no template variables exist
      if (vars.length === 0 && selectedSourceMeta.fields && selectedSourceMeta.fields.length > 0) {
        selectedSourceMeta.fields.forEach((f) => {
          add(f.key, f.label ? `${f.label}` : f.key, selectedSourceMeta.label || selectedSource || 'Source Field');
        });
      }
    }

    return items;
  }, [selectedSourceMeta, selectedSource]);

  // Format key like "current_value.install_location" into friendly label "Install Location"
  const formatKeyToLabel = (key: string): string => {
    if (!key) return '';
    const lastPart = key.includes('.') ? key.split('.').pop() || key : key;
    return lastPart
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  // Build SelectOptions for Table Column variable dropdown - matches Insert Variable data exactly
  const getTableVarOptions = (
    colField: string,
    cIdx: number,
    widgetCols: TableColumn[] = []
  ): SelectOption[] => {
    const list: SelectOption[] = [];

    // Support existing / legacy key if not in allSourceKeyItems
    if (colField && !allSourceKeyItems.some((v) => v.key === colField)) {
      list.push({
        value: colField,
        label: colField,
        category: 'Current Variable',
      });
    }

    allSourceKeyItems.forEach((v) => {
      const isSelectedElsewhere = widgetCols.some(
        (c, idx) => idx !== cIdx && c.field === v.key
      );
      list.push({
        value: v.key,
        label: v.key,
        category: v.category || 'General',
        disabled: isSelectedElsewhere,
        badge: isSelectedElsewhere ? '(Added)' : undefined,
      });
    });

    return list;
  };

  // Header variable insertion dropdown states & handlers
  const [showHeaderVarDropdown, setShowHeaderVarDropdown] = useState<boolean>(false);
  const [headerVarFilter, setHeaderVarFilter] = useState<string>('');
  const headerDropdownRef = useRef<HTMLDivElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);

  // Close header variable dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerDropdownRef.current && !headerDropdownRef.current.contains(e.target as Node)) {
        setShowHeaderVarDropdown(false);
      }
    };
    if (showHeaderVarDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHeaderVarDropdown]);

  const filteredHeaderVars = useMemo(() => {
    if (!headerVarFilter.trim()) return allSourceKeyItems;
    const q = headerVarFilter.toLowerCase();
    return allSourceKeyItems.filter(
      (v) =>
        v.key.toLowerCase().includes(q) ||
        (v.label && v.label.toLowerCase().includes(q)) ||
        (v.category && v.category.toLowerCase().includes(q))
    );
  }, [allSourceKeyItems, headerVarFilter]);

  const groupedHeaderVars = useMemo(() => {
    const groups: Record<string, typeof allSourceKeyItems> = {};
    filteredHeaderVars.forEach((item) => {
      const cat = item.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [filteredHeaderVars]);


  const handleInsertHeaderVar = (v: string) => {
    const formatted = `{{ ${v.trim()} }}`;
    const input = headerInputRef.current;
    if (input) {
      const start = input.selectionStart ?? (currentConfig.header || '').length;
      const end = input.selectionEnd ?? (currentConfig.header || '').length;
      const oldVal = currentConfig.header || '';
      const newVal = oldVal.substring(0, start) + formatted + oldVal.substring(end);
      setCurrentConfig((prev) => ({ ...prev, header: newVal }));
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + formatted.length, start + formatted.length);
      }, 0);
    } else {
      setCurrentConfig((prev) => ({
        ...prev,
        header: `${prev.header || ''} ${formatted}`.trim(),
      }));
    }
    setShowHeaderVarDropdown(false);
    setHeaderVarFilter('');
  };

  // Copy Open Template config into Close Template
  const handleCopyOpenToClose = () => {
    setCloseConfig({
      ...openConfig,
      header: openConfig.header || '',
      header_text_color: '#10b981',
      primary_color: '#10b981',
    });
    toast.success('Copied Open Template layout to Close Template!');
  };

  // Add Widget
  const handleAddWidget = (type: 'text' | 'table' | 'divider') => {
    if (isReadOnly) {
      toast.error('Global templates are read-only at client level. Please duplicate to edit.');
      return;
    }
    const newId = `${type}_${Date.now()}`;
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      if (type === 'text') {
        widgets.push({
          id: newId,
          type: 'text',
          content: '',
        } as TextWidget);
      } else if (type === 'divider') {
        widgets.push({
          id: newId,
          type: 'divider',
        } as DividerWidget);
      } else if (type === 'table') {
        widgets.push({
          id: newId,
          type: 'table',
          title: '',
          source: 'items',
          item_alias: 'item',
          columns: [],
          empty_message: '',
        } as TableWidget);
      }
      return { ...prev, widgets };
    });
  };

  // Drag & drop widget reordering
  const [draggedWidgetIndex, setDraggedWidgetIndex] = useState<number | null>(null);
  const [dragOverWidgetIndex, setDragOverWidgetIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedWidgetIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (draggedWidgetIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverWidgetIndex !== index) {
      setDragOverWidgetIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedWidgetIndex === null || draggedWidgetIndex === dropIndex) {
      setDraggedWidgetIndex(null);
      setDragOverWidgetIndex(null);
      return;
    }

    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      const [movedItem] = widgets.splice(draggedWidgetIndex, 1);
      widgets.splice(dropIndex, 0, movedItem);
      return { ...prev, widgets };
    });

    setDraggedWidgetIndex(null);
    setDragOverWidgetIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedWidgetIndex(null);
    setDragOverWidgetIndex(null);
  };

  // Drag & drop column reordering within a Table Widget
  const [draggedColumnInfo, setDraggedColumnInfo] = useState<{ widgetIndex: number; colIndex: number } | null>(null);
  const [dragOverColumnInfo, setDragOverColumnInfo] = useState<{ widgetIndex: number; colIndex: number } | null>(null);

  const handleColumnDragStart = (e: React.DragEvent, widgetIndex: number, colIndex: number) => {
    e.stopPropagation();
    setDraggedColumnInfo({ widgetIndex, colIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `col_${widgetIndex}_${colIndex}`);
  };

  const handleColumnDragOver = (e: React.DragEvent, widgetIndex: number, colIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (
      dragOverColumnInfo?.widgetIndex !== widgetIndex ||
      dragOverColumnInfo?.colIndex !== colIndex
    ) {
      setDragOverColumnInfo({ widgetIndex, colIndex });
    }
  };

  const handleColumnDrop = (e: React.DragEvent, widgetIndex: number, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !draggedColumnInfo ||
      draggedColumnInfo.widgetIndex !== widgetIndex ||
      draggedColumnInfo.colIndex === dropIndex
    ) {
      setDraggedColumnInfo(null);
      setDragOverColumnInfo(null);
      return;
    }

    const fromIndex = draggedColumnInfo.colIndex;
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      const target = { ...widgets[widgetIndex] } as TableWidget;
      const cols = [...(target.columns || [])];
      const [movedCol] = cols.splice(fromIndex, 1);
      cols.splice(dropIndex, 0, movedCol);
      target.columns = cols;
      widgets[widgetIndex] = target;
      return { ...prev, widgets };
    });

    setDraggedColumnInfo(null);
    setDragOverColumnInfo(null);
  };

  const handleColumnDragEnd = (e: React.DragEvent) => {
    e.stopPropagation();
    setDraggedColumnInfo(null);
    setDragOverColumnInfo(null);
  };

  // Delete Widget
  const handleDeleteWidget = (index: number) => {
    if (isReadOnly) {
      toast.error('Global templates are read-only at client level. Please duplicate to edit.');
      return;
    }
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      widgets.splice(index, 1);
      return { ...prev, widgets };
    });
  };

  // Update Text Widget Content
  const handleUpdateTextContent = (widgetIndex: number, content: string) => {
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      const target = { ...widgets[widgetIndex] } as TextWidget;
      target.content = content;
      widgets[widgetIndex] = target;
      return { ...prev, widgets };
    });
  };

  // Update Table Widget properties
  const handleUpdateTableWidget = (
    widgetIndex: number,
    updates: Partial<TableWidget>
  ) => {
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      const target = { ...widgets[widgetIndex] } as TableWidget;
      widgets[widgetIndex] = { ...target, ...updates };
      return { ...prev, widgets };
    });
  };

  // Add Column to Table Widget
  const handleAddTableColumn = (widgetIndex: number) => {
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      const target = { ...widgets[widgetIndex] } as TableWidget;
      const cols = [...(target.columns || [])];

      // Auto-suggest the first unused template variable from allSourceKeyItems if available
      const unusedVar = allSourceKeyItems.find(
        (v) => !cols.some((c) => c.field === v.key)
      );
      const defaultField = unusedVar ? unusedVar.key : '';
      const defaultLabel = unusedVar ? formatKeyToLabel(unusedVar.key) : '';

      cols.push({
        label: defaultLabel,
        field: defaultField,
        align: 'left',
      });
      target.columns = cols;
      widgets[widgetIndex] = target;
      return { ...prev, widgets };
    });
  };

  // Remove Column from Table Widget
  const handleRemoveTableColumn = (widgetIndex: number, colIndex: number) => {
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      const target = { ...widgets[widgetIndex] } as TableWidget;
      const cols = [...(target.columns || [])];
      cols.splice(colIndex, 1);
      target.columns = cols;
      widgets[widgetIndex] = target;
      return { ...prev, widgets };
    });
  };

  // Update Column inside Table Widget
  const handleUpdateColumn = (
    widgetIndex: number,
    colIndex: number,
    updates: Partial<TableColumn>
  ) => {
    setCurrentConfig((prev) => {
      const widgets = [...(prev.widgets || [])];
      const target = { ...widgets[widgetIndex] } as TableWidget;
      const cols = [...(target.columns || [])];

      // Prevent duplicate variable selection
      if (updates.field && updates.field.trim() !== '') {
        const isDuplicate = cols.some(
          (c, idx) => idx !== colIndex && c.field?.toLowerCase() === updates.field?.toLowerCase()
        );
        if (isDuplicate) {
          toast.error(`"${updates.field}" is already used in another column.`);
          return prev;
        }
      }

      const newCol = { ...cols[colIndex], ...updates };

      // If user picks a variable from the dropdown and label was empty, auto-sync label
      if (updates.field && !updates.label && !cols[colIndex]?.label) {
        newCol.label = formatKeyToLabel(updates.field);
      }

      cols[colIndex] = newCol;
      target.columns = cols;
      widgets[widgetIndex] = target;
      return { ...prev, widgets };
    });
  };

  // Save / Update Template
  const handleSaveTemplate = async () => {
    if (isReadOnly) {
      toast.error('Global templates cannot be edited at client level. Please duplicate to customize.');
      return;
    }
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!selectedSource) {
      toast.error('Source selection is required');
      return;
    }

    setSaving(true);
    const loader = toast.loading(isEditing ? 'Updating template...' : 'Creating template...');

    try {
      const finalIsGlobal = isEditing ? Boolean(isGlobal) : isGlobalLevel;
      const finalClientId = !finalIsGlobal ? (activeClientId || getClientId()) : undefined;

      const payload = {
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        source: selectedSource.toLowerCase(),
        is_active: isActive,
        is_global: finalIsGlobal,
        client_id: finalClientId,
        open_template: sanitizeTemplateConfig(openConfig, DEFAULT_TEMPLATE_CONFIG),
        close_template: sanitizeTemplateConfig(closeConfig, DEFAULT_CLOSE_CONFIG),
      };

      if (isEditing && id) {
        await htmlTemplatesApi.updateTemplate(id, payload);
        toast.success('Template updated successfully', { id: loader });
        sessionStorage.removeItem(DRAFT_META_KEY);
      } else {
        const res = await htmlTemplatesApi.createTemplate(payload);
        toast.success('Template created successfully', { id: loader });
        sessionStorage.removeItem(DRAFT_META_KEY);
        const newId = res.data?.id || (res as any)?.id;
        if (newId) {
          navigate(`/automation/templates/editor/${newId}`, { replace: true });
        }
      }
    } catch (err: any) {
      console.error('Failed to save template:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to save template', {
        id: loader,
      });
    } finally {
      setSaving(false);
    }
  };

  // Duplicate global template to client level directly from editor
  const handleDuplicateFromEditor = async () => {
    const loader = toast.loading('Duplicating template for this client...');
    try {
      const finalClientId = activeClientId || getClientId();
      const payload = {
        name: `${templateName} (Copy)`,
        description: templateDescription,
        source: selectedSource.toLowerCase(),
        is_active: true,
        is_global: false,
        client_id: finalClientId,
        open_template: sanitizeTemplateConfig(openConfig, DEFAULT_TEMPLATE_CONFIG),
        close_template: sanitizeTemplateConfig(closeConfig, DEFAULT_CLOSE_CONFIG),
      };
      const res = await htmlTemplatesApi.createTemplate(payload);
      toast.success('Template duplicated! You can now edit your client copy.', { id: loader });
      const newId = res.data?.id || (res as any)?.id;
      if (newId) {
        navigate(`/automation/templates/editor/${newId}`, { replace: true });
      }
    } catch (err: any) {
      console.error('Failed to duplicate template:', err);
      toast.error(err.response?.data?.message || 'Failed to duplicate template', { id: loader });
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white space-y-3">
        <div className="w-9 h-9 border-3 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Loading Template Editor...
        </span>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 font-sans flex flex-col overflow-hidden pb-16">
      {/* Top Header & Action Bar */}
      <header className="flex-none bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-2 flex items-center justify-between gap-4 z-30 shadow-sm dark:shadow-md">
        {/* Left: Back button & Template Title */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/automation?tab=templates')}
            className="p-1.5 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-700 transition-colors cursor-pointer"
            title="Back to Templates"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-md bg-orange-50 dark:bg-orange-600/20 border border-orange-200 dark:border-orange-600/30 text-orange-600 dark:text-orange-400 flex-none">
              <FileCode2 className="w-4 h-4" />
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <h1
                className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-xs md:max-w-sm"
                title={templateName}
              >
                {templateName}
              </h1>
              {isGlobal ? (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 flex-none">
                  Global
                </span>
              ) : (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-500/20 flex-none">
                  {currentLevel?.name || 'Client'}
                </span>
              )}
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={handleOpenEditMetaModal}
                  className="p-1 rounded text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer flex-none"
                  title="Edit name and description"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Center: Dual Tabs Selector (Open vs Close alert) */}
        <div className="hidden md:flex items-center bg-gray-100 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md p-1 gap-1">
          <button
            type="button"
            onClick={() => setActiveTab('open')}
            className={`px-3 py-1 rounded text-xs font-bold transition-all cursor-pointer ${activeTab === 'open'
                ? 'bg-orange-600 text-white shadow-md shadow-orange-600/20'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
          >
            Open Alert Layout
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('close')}
            className={`px-3 py-1 rounded text-xs font-bold transition-all cursor-pointer ${activeTab === 'close'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
          >
            Close / Resolved Layout
          </button>
        </div>

        {/* Right: View Mode & Save / Duplicate Button */}
        <div className="flex items-center gap-2">
          {/* View Mode Switcher */}
          <div className="hidden lg:flex items-center bg-gray-100 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('editor')}
              className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${viewMode === 'editor' ? 'bg-orange-600 dark:bg-orange-500 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              title="Editor View Only"
            >
              <Sliders className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('split')}
              className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${viewMode === 'split' ? 'bg-orange-600 dark:bg-orange-500 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              title="Split View (Editor + Live Preview)"
            >
              <Columns2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('preview')}
              className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${viewMode === 'preview' ? 'bg-orange-600 dark:bg-orange-500 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              title="Preview View Only"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Save or Duplicate to Edit Button */}
          {isReadOnly ? (
            <button
              type="button"
              onClick={handleDuplicateFromEditor}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs bg-amber-500 hover:bg-amber-400 text-gray-950 shadow-md transition-all cursor-pointer"
              title="Duplicate this global template to edit for this client"
            >
              <Copy className="w-3.5 h-3.5" />
              <span>Duplicate to Edit</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSaveTemplate}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs bg-orange-600 hover:bg-orange-500 dark:bg-orange-500 dark:hover:bg-orange-600 text-white shadow-md shadow-orange-500/20 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{saving ? 'Saving...' : 'Save Template'}</span>
            </button>
          )}
        </div>
      </header>

      {/* Read-Only Warning Banner for Global Template in Client Level */}
      {isReadOnly && (
        <div className="flex-none bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20 px-4 py-2 flex items-center justify-between gap-3 text-xs text-amber-800 dark:text-amber-200">
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0" />
            <span className="truncate">
              <strong>Global Template (Read-Only):</strong> Global templates cannot be modified at client level. Duplicate this template to customize it for {currentLevel?.name || 'this client'}.
            </span>
          </div>
          <button
            type="button"
            onClick={handleDuplicateFromEditor}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-gray-950 font-bold rounded text-[11px] transition-colors cursor-pointer shrink-0 shadow"
          >
            <Copy className="w-3 h-3" />
            Duplicate Template
          </button>
        </div>
      )}

      {/* Mobile Tab Switcher */}
      <div className="md:hidden flex-none bg-white dark:bg-gray-900 px-3 py-1.5 border-b border-gray-200 dark:border-gray-800 flex justify-center gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('open')}
          className={`px-3 py-1 rounded-lg text-xs font-bold ${activeTab === 'open' ? 'bg-orange-600 text-white' : 'text-gray-500 dark:text-gray-400'
            }`}
        >
          Open Alert
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('close')}
          className={`px-3 py-1 rounded-lg text-xs font-bold ${activeTab === 'close' ? 'bg-emerald-600 text-white' : 'text-gray-500 dark:text-gray-400'
            }`}
        >
          Close Alert
        </button>
      </div>

      {/* Main Workspace Area */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* 1. Canva-styled Left Sidebar Rail */}
        {(viewMode === 'editor' || viewMode === 'split') && (
          <div className="w-14 flex-none bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 flex flex-col items-center py-2.5 gap-2 z-20 select-none">
            {/* Widgets / Elements Tab */}
            <button
              type="button"
              onClick={() => setSidebarTab('widgets')}
              className={`w-10 h-10 rounded-md flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer ${
                sidebarTab === 'widgets'
                  ? 'bg-orange-50 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/40 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-900 border border-transparent'
              }`}
              title="Widgets & Content"
            >
              <Layers className="w-3.5 h-3.5" />
              <span className="text-[9px] font-semibold tracking-tight">Widgets</span>
            </button>

            {/* Theme Tab */}
            <button
              type="button"
              onClick={() => setSidebarTab('theme')}
              className={`w-10 h-10 rounded-md flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer ${
                sidebarTab === 'theme'
                  ? 'bg-orange-50 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/40 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-900 border border-transparent'
              }`}
              title="Theme Settings"
            >
              <Palette className="w-3.5 h-3.5" />
              <span className="text-[9px] font-semibold tracking-tight">Theme</span>
            </button>
          </div>
        )}

        {/* 2. Main Workspace Editor Pane (Widgets or Theme based on sidebar rail) */}
        {(viewMode === 'editor' || viewMode === 'split') && (
          <div
            className={`flex-1 flex flex-col min-w-0 border-r border-gray-200 dark:border-gray-800/80 overflow-hidden ${
              viewMode === 'split' ? 'max-w-3xl' : 'w-full'
            }`}
          >
            {/* Fixed Subheader directly touching topbar (Zero gap) */}
            <div className="flex-none bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-3.5 py-1.5 flex items-center justify-between z-10 select-none shadow-sm">
              {sidebarTab === 'widgets' ? (
                <>
                  <div className="flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                    <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Widgets & Layout
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                      ({currentConfig.widgets?.length || 0} widgets)
                    </span>
                  </div>

                  {/* Top Right Fixed Widget Actions */}
                  {!isReadOnly ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          handleAddWidget('text');
                          toast.success('Added text block');
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-orange-50 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 transition-all cursor-pointer shadow-sm active:scale-95"
                      >
                        <Type className="w-3.5 h-3.5" />
                        <span>+ Text</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleAddWidget('table');
                          toast.success('Added data table');
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-cyan-50 dark:bg-cyan-500/10 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-500/30 transition-all cursor-pointer shadow-sm active:scale-95"
                      >
                        <TableIcon className="w-3.5 h-3.5" />
                        <span>+ Table</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleAddWidget('divider');
                          toast.success('Added divider');
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 transition-all cursor-pointer shadow-sm active:scale-95"
                      >
                        <Minus className="w-3.5 h-3.5" />
                        <span>+ Divider</span>
                      </button>
                    </div>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
                      View Only
                    </span>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Palette className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                    <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Theme & Canvas Settings
                    </span>
                  </div>
                  <span className="text-[10.5px] font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800/80 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700">
                    Max Width: {currentConfig.max_width || '640px'}
                  </span>
                </>
              )}
            </div>

            {/* Scrollable Canvas Area below the fixed bar */}
            <div className={`flex-1 overflow-y-auto custom-scroll p-3 ${isReadOnly ? 'pointer-events-none opacity-85 select-none' : ''}`}>
              <div className={`space-y-3 ${viewMode === 'editor' ? 'max-w-4xl mx-auto' : ''}`}>
                {/* WIDGETS VIEW */}
                {sidebarTab === 'widgets' && (
                  <>
                  {/* 1. Header & Branding Configuration */}
            <div
              onClick={() => isHeaderCollapsed && setIsHeaderCollapsed(false)}
              className={`bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800 rounded-md p-3 shadow-sm dark:shadow-md space-y-2.5 transition-colors ${
                isHeaderCollapsed ? 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-700' : ''
              }`}
            >
                  <div
                    className="flex items-center justify-between pb-1 border-b border-gray-100 dark:border-gray-800/80 cursor-pointer select-none"
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('button')) return;
                      setIsHeaderCollapsed(!isHeaderCollapsed);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                      <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Header & Branding
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      {activeTab === 'close' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyOpenToClose();
                          }}
                          className="flex items-center gap-1 text-[11px] font-semibold text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 transition-colors cursor-pointer mr-1"
                        >
                          <Copy className="w-3 h-3" />
                          <span>Copy Layout from Open Alert</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsHeaderCollapsed(!isHeaderCollapsed);
                        }}
                        className="p-1 rounded-md text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                        title={isHeaderCollapsed ? 'Expand Header' : 'Collapse Header'}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isHeaderCollapsed ? '' : 'rotate-180'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {!isHeaderCollapsed && (
                    <div className="space-y-3.5 pt-1">
                      {/* Header Title with Variable Inserter */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                            Header Title <span className="text-gray-400 dark:text-gray-500 font-normal">(supports variables)</span>
                          </label>

                          {/* Variable Inserter Dropdown */}
                          <div className="relative" ref={headerDropdownRef}>
                            <button
                              type="button"
                              onClick={() => setShowHeaderVarDropdown(!showHeaderVarDropdown)}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-orange-50 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 transition-all cursor-pointer shadow-sm"
                            >
                              <Sparkles className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                              <span>Insert Variable</span>
                              <ChevronDown className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                            </button>

                            {showHeaderVarDropdown && (
                              <div className="absolute right-0 top-full mt-1.5 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-2xl z-50 p-2 text-xs flex flex-col space-y-1.5 animate-in fade-in zoom-in-95 duration-150">
                                <div className="flex items-center justify-between pb-1 border-b border-gray-100 dark:border-gray-800">
                                  <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">
                                    Variables ({selectedSource})
                                  </span>
                                  <span className="text-[9px] text-orange-600 dark:text-orange-400 font-mono">
                                    {filteredHeaderVars.length} keys
                                  </span>
                                </div>

                                {/* Search filter for variables */}
                                <div className="relative">
                                  <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                                  <input
                                    type="text"
                                    value={headerVarFilter}
                                    onChange={(e) => setHeaderVarFilter(e.target.value)}
                                    placeholder="Search variables..."
                                    className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md pl-7 pr-2 py-1 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500"
                                    autoFocus
                                  />
                                </div>

                                <div className="max-h-56 overflow-y-auto custom-scroll space-y-2 pt-1">
                                  {filteredHeaderVars.length === 0 ? (
                                    <div className="py-3 text-center text-[11px] text-gray-500">
                                      No matching variables
                                    </div>
                                  ) : (
                                    Object.entries(groupedHeaderVars).map(([catName, items]) => (
                                      <div key={catName} className="space-y-0.5">
                                        <div className="px-2 py-0.5 text-[9px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider bg-orange-50 dark:bg-orange-500/10 rounded flex items-center justify-between mb-1">
                                          <span className="truncate">{catName}</span>
                                          <span className="text-[8px] font-mono text-orange-600/60 dark:text-orange-400/60 font-normal">
                                            {items.length}
                                          </span>
                                        </div>
                                        {items.map((item) => (
                                          <button
                                            key={item.key}
                                            type="button"
                                            onClick={() => handleInsertHeaderVar(item.key)}
                                            className="w-full text-left px-2 py-1 rounded hover:bg-orange-50 dark:hover:bg-orange-500/20 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
                                          >
                                            <div className="truncate pr-2">
                                              <div className="text-[11px] font-mono text-cyan-700 dark:text-cyan-300">
                                                {`{{ ${item.key} }}`}
                                              </div>
                                              {item.label && item.label !== item.key && (
                                                <div className="text-[9px] text-gray-500 dark:text-gray-400 truncate">
                                                  {item.label}
                                                </div>
                                              )}
                                            </div>
                                            <span className="text-[9px] text-gray-400 group-hover:text-orange-600 dark:group-hover:text-orange-400 shrink-0 font-sans">
                                              Insert
                                            </span>
                                          </button>
                                        ))}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <input
                          ref={headerInputRef}
                          type="text"
                          value={currentConfig.header || ''}
                          onChange={(e) => setCurrentConfig((prev) => ({ ...prev, header: e.target.value }))}
                          placeholder="e.g. Critical Port {{ asset.name }} Alert"
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500/80 focus:ring-1 focus:ring-orange-500/40 transition-all"
                        />
                      </div>

                      {/* Typography Bar (Alignment, Font Size, Font Weight) */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                        {/* Text Alignment */}
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                            Text Alignment
                          </label>
                          <div className="h-[38px] flex items-center bg-gray-100 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg p-1 gap-1">
                            {[
                              { id: 'left', label: 'Left', icon: AlignLeft },
                              { id: 'center', label: 'Center', icon: AlignCenter },
                              { id: 'right', label: 'Right', icon: AlignRight },
                            ].map((item) => {
                              const Icon = item.icon;
                              const isActive = (currentConfig.header_align || 'left') === item.id;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => setCurrentConfig((prev) => ({ ...prev, header_align: item.id as any }))}
                                  className={`flex-1 h-full flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                                    isActive
                                      ? 'bg-orange-600 dark:bg-orange-500 text-white shadow-sm'
                                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/60 dark:hover:bg-gray-900/60'
                                  }`}
                                >
                                  <Icon className="w-3.5 h-3.5" />
                                  <span>{item.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Font Size */}
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                            Font Size
                          </label>
                          <CustomSelect
                            options={FONT_SIZE_OPTIONS}
                            value={
                              currentConfig.header_font_size && currentConfig.header_font_size.includes('px')
                                ? currentConfig.header_font_size
                                : currentConfig.header_font_size
                                ? `${currentConfig.header_font_size}px`
                                : '20px'
                            }
                            onChange={(val: string) =>
                              setCurrentConfig((prev) => ({ ...prev, header_font_size: val }))
                            }
                            placeholder="Select Font Size"
                            className="w-full"
                          />
                        </div>

                        {/* Font Weight */}
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                            Font Weight
                          </label>
                          <CustomSelect
                            options={FONT_WEIGHT_OPTIONS}
                            value={
                              currentConfig.header_font_weight === 'bold'
                                ? '700'
                                : currentConfig.header_font_weight === 'normal'
                                ? '400'
                                : currentConfig.header_font_weight || '700'
                            }
                            onChange={(val: string) =>
                              setCurrentConfig((prev) => ({ ...prev, header_font_weight: val }))
                            }
                            placeholder="Select Font Weight"
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Brand Logo Section */}
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                            Brand Logo <span className="text-gray-400 dark:text-gray-500 font-normal">(Optional)</span>
                          </label>
                          {currentConfig.logo_url && (
                            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-500/20">
                              <Check className="w-3 h-3" /> Logo Added
                            </span>
                          )}
                        </div>

                        <input
                          ref={logoFileInputRef}
                          type="file"
                          accept=".png,.jpg,.jpeg,.svg,.webp,.gif,.ico,image/png,image/jpeg,image/svg+xml,image/webp,image/gif,image/x-icon"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleLogoFileUpload(file);
                          }}
                          className="hidden"
                        />

                        {currentConfig.logo_url ? (
                          <div className="space-y-3">
                            {/* Preview Card */}
                            <div className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl p-3.5 flex items-center justify-between gap-3 shadow-inner">
                              <div className="flex items-center gap-3 min-w-0">
                                {/* Thumbnail */}
                                <div className="w-12 h-12 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 flex items-center justify-center p-1.5 overflow-hidden shrink-0">
                                  <img
                                    src={currentConfig.logo_url}
                                    alt="Brand Logo"
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => {
                                      (e.target as HTMLElement).style.display = 'none';
                                    }}
                                  />
                                </div>

                                {/* Details */}
                                <div className="min-w-0 space-y-0.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate max-w-xs">
                                      {(currentConfig.logo_url.split('/').pop() || 'Brand Logo').replace(/^[0-9a-fA-F-]+_/, '')}
                                    </span>
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                                      Ready
                                    </span>
                                  </div>
                                  <p className="text-[10.5px] text-gray-500">
                                    Attached to email header
                                  </p>
                                </div>
                              </div>

                              {/* Actions */}
                              {!isReadOnly && (
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    disabled={isUploadingLogo}
                                    onClick={() => logoFileInputRef.current?.click()}
                                    className="px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 border border-gray-200 dark:border-gray-800 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow-sm"
                                  >
                                    <Upload className="w-3.5 h-3.5" /> Replace
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isUploadingLogo}
                                    onClick={() => setCurrentConfig((prev) => ({ ...prev, logo_url: '' }))}
                                    className="px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-200 dark:border-red-500/20 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow-sm"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Logo Layout Controls (Position & Width) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                  Logo Position
                                </label>
                                <CustomSelect
                                  options={LOGO_POSITION_OPTIONS}
                                  value={currentConfig.logo_position || 'inline-left'}
                                  onChange={(val: string) =>
                                    setCurrentConfig((prev) => ({
                                      ...prev,
                                      logo_position: val as any,
                                    }))
                                  }
                                  placeholder="Select Logo Position"
                                  className="w-full"
                                />
                              </div>

                              <div>
                                <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                  Logo Width
                                </label>
                                <CustomSelect
                                  options={LOGO_WIDTH_OPTIONS}
                                  value={
                                    currentConfig.logo_width && currentConfig.logo_width.includes('px')
                                      ? currentConfig.logo_width
                                      : currentConfig.logo_width
                                      ? `${currentConfig.logo_width}px`
                                      : '120px'
                                  }
                                  onChange={(val: string) =>
                                    setCurrentConfig((prev) => ({ ...prev, logo_width: val }))
                                  }
                                  placeholder="Select Logo Width"
                                  className="w-full"
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Full-width Dropzone */
                          <div
                            onDragOver={handleLogoDragOver}
                            onDragLeave={handleLogoDragLeave}
                            onDrop={handleLogoDrop}
                            onClick={() => !isUploadingLogo && logoFileInputRef.current?.click()}
                            className={`w-full border-2 border-dashed rounded-xl p-5 text-center transition-all cursor-pointer select-none flex flex-col items-center justify-center gap-2.5 ${
                              isDraggingLogo
                                ? 'border-orange-500 bg-orange-500/10 scale-[0.99]'
                                : isUploadingLogo
                                ? 'border-gray-300 dark:border-gray-700 bg-gray-100/50 dark:bg-gray-900/50 cursor-wait opacity-80'
                                : 'border-gray-300 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-950/60 hover:border-orange-500/50 hover:bg-orange-50/20 dark:hover:bg-gray-900/40'
                            }`}
                          >
                            {isUploadingLogo ? (
                              <div className="flex flex-col items-center gap-2 py-2">
                                <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                                <span className="text-xs font-semibold text-gray-900 dark:text-white">
                                  Uploading logo to storage...
                                </span>
                                <span className="text-[10.5px] text-gray-500 dark:text-gray-400">Please wait while image is being processed</span>
                              </div>
                            ) : (
                              <>
                                <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 flex items-center justify-center text-orange-600 dark:text-orange-400 shadow-sm transition-transform group-hover:scale-110">
                                  <Upload className="w-5 h-5" />
                                </div>
                                <div className="space-y-0.5">
                                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                                    Click to upload <span className="text-gray-400 dark:text-gray-500 font-normal">or drag & drop</span>
                                  </p>
                                  <p className="text-[10.5px] text-gray-500">
                                    PNG, JPEG, SVG, WEBP, GIF, ICO (Max 15MB)
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Widgets List (Draggable & First-class) */}
                {currentConfig.widgets?.map((widget, wIdx) => {
                  const widgetKey = widget.id || `widget_${wIdx}`;
                  const isCollapsed = Boolean(collapsedWidgets[widgetKey]);

                  return (
                    <div
                      key={widgetKey}
                      onDragOver={(e) => handleDragOver(e, wIdx)}
                      onDrop={(e) => handleDrop(e, wIdx)}
                      onClick={() => isCollapsed && toggleWidgetCollapse(widgetKey)}
                      className={`bg-white dark:bg-gray-900/70 border rounded-md overflow-hidden shadow-sm dark:shadow-md transition-all ${
                        isCollapsed ? 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-700' : ''
                      } ${
                        draggedWidgetIndex === wIdx
                          ? 'opacity-40 border-dashed border-orange-500 scale-[0.99]'
                          : dragOverWidgetIndex === wIdx
                          ? 'border-orange-500 ring-2 ring-orange-500/30 bg-orange-50/20 dark:bg-gray-900'
                          : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700/80'
                      }`}
                    >
                      {/* Widget Header with Drag Handle */}
                      <div
                        draggable={true}
                        onDragStart={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('button') || target.closest('input') || target.closest('select')) {
                            e.preventDefault();
                            return;
                          }
                          handleDragStart(e, wIdx);
                        }}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('button') || target.closest('.drag-handle')) {
                            return;
                          }
                          toggleWidgetCollapse(widgetKey);
                        }}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50/90 dark:bg-gray-900/90 border-b border-gray-200 dark:border-gray-800/80 cursor-pointer select-none"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="drag-handle p-1 -ml-1 rounded hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors cursor-grab active:cursor-grabbing"
                            title="Drag to reorder"
                          >
                            <GripVertical className="w-3.5 h-3.5" />
                          </div>

                          <span className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 flex items-center justify-center text-[10px] font-bold font-mono">
                            {wIdx + 1}
                          </span>

                          <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                            {widget.type === 'text' && <Type className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />}
                            {widget.type === 'table' && <TableIcon className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />}
                            {widget.type === 'divider' && <Minus className="w-3.5 h-3.5 text-gray-400" />}
                            {widget.type === 'table' && (widget as TableWidget).title
                              ? `TABLE: ${(widget as TableWidget).title}`
                              : `${widget.type.toUpperCase()} WIDGET`}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleWidgetCollapse(widgetKey);
                            }}
                            className="p-1 rounded-md text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/60 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                            title={isCollapsed ? 'Expand Widget' : 'Collapse Widget'}
                          >
                            <ChevronDown
                              className={`w-4 h-4 transition-transform duration-200 ${
                                isCollapsed ? '' : 'rotate-180'
                              }`}
                            />
                          </button>

                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteWidget(wIdx);
                              }}
                              className="p-1 rounded-md text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors cursor-pointer ml-0.5"
                              title="Delete Widget"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Widget Body */}
                      {!isCollapsed && (
                        <div className="p-3 space-y-2.5">
                          {/* 1. Text Widget Content with Tiptap */}
                          {widget.type === 'text' && (
                            <div className="space-y-1">
                              <TiptapRichTextEditor
                                value={(widget as TextWidget).content || ''}
                                onChange={(html) => handleUpdateTextContent(wIdx, html)}
                                templateVariables={allSourceKeyItems}
                                placeholder="Enter notification content..."
                                minHeight="110px"
                              />
                            </div>
                          )}

                          {/* 2. Divider Widget */}
                          {widget.type === 'divider' && (
                            <div className="py-1.5">
                              <div className="border-t border-gray-200 dark:border-gray-700" />
                            </div>
                          )}

                          {/* 3. Table Widget */}
                          {widget.type === 'table' && (
                            <div className="space-y-3">
                              {/* Section Title */}
                              <div className="text-xs">
                                <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1">
                                  Table Section Title
                                </label>
                                <input
                                  type="text"
                                  value={(widget as TableWidget).title || ''}
                                  onChange={(e) =>
                                    handleUpdateTableWidget(wIdx, { title: e.target.value })
                                  }
                                  placeholder="e.g. Asset Details / Ports (Optional)"
                                  className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-cyan-500/50"
                                />
                              </div>

                              {/* Columns Configuration Table */}
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                                    Columns ({(widget as TableWidget).columns?.length || 0})
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleAddTableColumn(wIdx)}
                                    className="flex items-center gap-1 text-[10px] font-bold text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 cursor-pointer"
                                  >
                                    <Plus className="w-3 h-3" />
                                    <span>Add Column</span>
                                  </button>
                                </div>

                                {/* Column Header Labels */}
                                {((widget as TableWidget).columns || []).length > 0 && (
                                  <div className="flex items-center gap-2 px-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    <span className="w-5"></span>
                                    <span className="flex-1">Column Header</span>
                                    <span className="flex-1">Variable</span>
                                    <span className="w-24">Align</span>
                                    <span className="w-7"></span>
                                  </div>
                                )}

                                {/* Columns list */}
                                <div className="space-y-1.5">
                                  {((widget as TableWidget).columns || []).map((col, cIdx) => {
                                    const isDraggedCol =
                                      draggedColumnInfo?.widgetIndex === wIdx &&
                                      draggedColumnInfo?.colIndex === cIdx;
                                    const isDragOverCol =
                                      dragOverColumnInfo?.widgetIndex === wIdx &&
                                      dragOverColumnInfo?.colIndex === cIdx;

                                    return (
                                      <div
                                        key={cIdx}
                                        draggable={true}
                                        onDragStart={(e) => {
                                          const target = e.target as HTMLElement;
                                          if (
                                            target.closest('input') ||
                                            target.closest('select') ||
                                            target.closest('button')
                                          ) {
                                            e.preventDefault();
                                            return;
                                          }
                                          handleColumnDragStart(e, wIdx, cIdx);
                                        }}
                                        onDragOver={(e) => handleColumnDragOver(e, wIdx, cIdx)}
                                        onDrop={(e) => handleColumnDrop(e, wIdx, cIdx)}
                                        onDragEnd={handleColumnDragEnd}
                                        className={`flex items-center gap-2 bg-gray-50 dark:bg-gray-950/80 p-1.5 rounded-md border text-xs transition-all ${
                                          isDraggedCol
                                            ? 'opacity-30 border-dashed border-cyan-500 bg-cyan-50 dark:bg-cyan-950/20'
                                            : isDragOverCol
                                            ? 'border-cyan-500 ring-1 ring-cyan-400/40 bg-cyan-50/40 dark:bg-gray-900'
                                            : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                                        }`}
                                      >
                                        {/* Drag Handle */}
                                        <div
                                          className="w-5 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800"
                                          title="Drag to reorder column"
                                        >
                                          <GripVertical className="w-3.5 h-3.5" />
                                        </div>

                                        {/* Column Header Label Input */}
                                        <input
                                          type="text"
                                          value={col.label || ''}
                                          onChange={(e) =>
                                            handleUpdateColumn(wIdx, cIdx, { label: e.target.value })
                                          }
                                          placeholder="e.g. Name"
                                          className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded px-2 py-1 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-cyan-500/50"
                                        />

                                        {/* Variable Dropdown */}
                                        <div className="flex-1 min-w-0">
                                          <CustomSelect
                                            value={col.field || ''}
                                            options={getTableVarOptions(
                                              col.field || '',
                                              cIdx,
                                              (widget as TableWidget).columns
                                            )}
                                            onChange={(selectedVal) => {
                                              if (!selectedVal) return;
                                              const alreadyExists = (
                                                widget as TableWidget
                                              ).columns?.some(
                                                (c, idx) => idx !== cIdx && c.field === selectedVal
                                              );
                                              if (alreadyExists) {
                                                toast.error(
                                                  `Variable "${selectedVal}" is already added as a column.`
                                                );
                                                return;
                                              }
                                              handleUpdateColumn(wIdx, cIdx, {
                                                field: selectedVal,
                                                label: col.label || formatKeyToLabel(selectedVal),
                                              });
                                            }}
                                            placeholder="Select Variable..."
                                            searchable
                                            searchPlaceholder="Search variables..."
                                            className="w-full"
                                            buttonClassName="!bg-white dark:!bg-gray-900 !border-gray-200 dark:!border-gray-800 !py-1 !px-2 !text-xs !font-mono !text-cyan-700 dark:!text-cyan-300 focus:!border-cyan-500/50 hover:!border-gray-300 dark:hover:!border-gray-700 !rounded"
                                          />
                                        </div>

                                        {/* Alignment */}
                                        <div className="w-24 flex-none">
                                          <CustomSelect
                                            value={col.align || 'left'}
                                            options={ALIGN_OPTIONS}
                                            onChange={(val) =>
                                              handleUpdateColumn(wIdx, cIdx, {
                                                align: val as any,
                                              })
                                            }
                                            placeholder="Align"
                                            className="w-full"
                                            buttonClassName="!bg-white dark:!bg-gray-900 !border-gray-200 dark:!border-gray-800 !py-1 !px-2 !text-xs !text-gray-700 dark:!text-gray-300 focus:!border-cyan-500/50 hover:!border-gray-300 dark:hover:!border-gray-700 !rounded"
                                          />
                                        </div>

                                        {/* Delete Column */}
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveTableColumn(wIdx, cIdx)}
                                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors cursor-pointer"
                                          title="Delete Column"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {(!currentConfig.widgets || currentConfig.widgets.length === 0) && (
                  <div className="py-6 text-center text-xs text-gray-500 border border-dashed border-gray-300 dark:border-gray-800 rounded-md space-y-2 bg-white/60 dark:bg-gray-900/40">
                    <p>No widgets added yet.</p>
                    {!isReadOnly && (
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleAddWidget('text')}
                          className="text-xs text-orange-600 dark:text-orange-400 underline cursor-pointer"
                        >
                          Add Text
                        </button>
                        <span>•</span>
                        <button
                          type="button"
                          onClick={() => handleAddWidget('table')}
                          className="text-xs text-cyan-600 dark:text-cyan-400 underline cursor-pointer"
                        >
                          Add Table
                        </button>
                        <span>•</span>
                        <button
                          type="button"
                          onClick={() => handleAddWidget('divider')}
                          className="text-xs text-gray-500 dark:text-gray-400 underline cursor-pointer"
                        >
                          Add Divider
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* 3. Footer Content */}
                <div
                  onClick={() => isFooterCollapsed && setIsFooterCollapsed(false)}
                  className={`bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800 rounded-md p-3 shadow-sm dark:shadow-md space-y-2 transition-colors ${
                    isFooterCollapsed ? 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-700' : ''
                  }`}
                >
                  <div
                    className="flex items-center justify-between pb-1 border-b border-gray-100 dark:border-gray-800/80 cursor-pointer select-none"
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('button')) return;
                      setIsFooterCollapsed(!isFooterCollapsed);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Type className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                      <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Footer Content
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 hidden sm:inline">
                        Rich text & variables supported
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsFooterCollapsed(!isFooterCollapsed);
                        }}
                        className="p-1 rounded-md text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                        title={isFooterCollapsed ? 'Expand Footer' : 'Collapse Footer'}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isFooterCollapsed ? '' : 'rotate-180'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {!isFooterCollapsed && (
                    <div className="pt-1">
                      <TiptapRichTextEditor
                        value={currentConfig.footer || ''}
                        onChange={(content) => setCurrentConfig((prev) => ({ ...prev, footer: content }))}
                        templateVariables={allSourceKeyItems}
                        placeholder="Sent automatically by Event Automation Engine • Confidential..."
                        minHeight="80px"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* THEME VIEW IN THE SAME PANEL */}
            {sidebarTab === 'theme' && (
              <div className="space-y-3.5">
                {/* 1. Quick Theme Presets */}
                <div className="bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Palette className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                      <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Theme Presets
                      </span>
                    </div>
                    <span className="text-[10.5px] text-gray-500 dark:text-gray-400">
                      Click to apply pre-configured palettes
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {/* White / Light Theme (Default) */}
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentConfig((prev) => ({
                          ...prev,
                          background_color: '#f8fafc',
                          card_background: '#ffffff',
                          text_color: '#111827',
                          muted_color: '#64748b',
                          border_color: '#e2e8f0',
                        }));
                        toast.success('White theme preset applied');
                      }}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-2 relative ${
                        (currentConfig.card_background || '#ffffff').toLowerCase() === '#ffffff' &&
                        (currentConfig.background_color || '#f8fafc').toLowerCase() === '#f8fafc'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 ring-1 ring-orange-500/30 shadow-sm'
                          : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/60 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-900/60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sun className="w-4 h-4 text-amber-500" />
                          <span className="text-xs font-bold text-gray-900 dark:text-white">White (Default)</span>
                        </div>
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                          Default
                        </span>
                      </div>
                      <p className="text-[10.5px] text-gray-500 dark:text-gray-400 leading-tight">
                        Clean white email card with soft light canvas
                      </p>
                      <div className="flex items-center gap-1.5 pt-1">
                        <div className="w-4 h-4 rounded-full border border-gray-300 bg-[#ffffff]" title="Card: #ffffff" />
                        <div className="w-4 h-4 rounded-full border border-gray-300 bg-[#f8fafc]" title="Canvas: #f8fafc" />
                        <div className="w-4 h-4 rounded-full border border-gray-700 bg-[#111827]" title="Text: #111827" />
                        <div className="w-4 h-4 rounded-full border border-gray-400 bg-[#e2e8f0]" title="Border: #e2e8f0" />
                      </div>
                    </button>

                    {/* Pure White */}
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentConfig((prev) => ({
                          ...prev,
                          background_color: '#ffffff',
                          card_background: '#ffffff',
                          text_color: '#111827',
                          muted_color: '#64748b',
                          border_color: '#e5e7eb',
                        }));
                        toast.success('Pure white preset applied');
                      }}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-2 relative ${
                        (currentConfig.card_background || '#ffffff').toLowerCase() === '#ffffff' &&
                        (currentConfig.background_color || '#f8fafc').toLowerCase() === '#ffffff'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 ring-1 ring-orange-500/30 shadow-sm'
                          : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/60 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-900/60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sun className="w-4 h-4 text-orange-500" />
                          <span className="text-xs font-bold text-gray-900 dark:text-white">Pure White</span>
                        </div>
                      </div>
                      <p className="text-[10.5px] text-gray-500 dark:text-gray-400 leading-tight">
                        Seamless 100% white body and background
                      </p>
                      <div className="flex items-center gap-1.5 pt-1">
                        <div className="w-4 h-4 rounded-full border border-gray-300 bg-[#ffffff]" title="Card: #ffffff" />
                        <div className="w-4 h-4 rounded-full border border-gray-300 bg-[#ffffff]" title="Canvas: #ffffff" />
                        <div className="w-4 h-4 rounded-full border border-gray-700 bg-[#111827]" title="Text: #111827" />
                        <div className="w-4 h-4 rounded-full border border-gray-400 bg-[#e5e7eb]" title="Border: #e5e7eb" />
                      </div>
                    </button>

                    {/* Dark Theme */}
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentConfig((prev) => ({
                          ...prev,
                          background_color: '#0b0f19',
                          card_background: '#111827',
                          text_color: '#f3f4f6',
                          muted_color: '#9ca3af',
                          border_color: '#1f2937',
                        }));
                        toast.success('Dark theme preset applied');
                      }}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-2 relative ${
                        (currentConfig.card_background || '').toLowerCase() === '#111827'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 ring-1 ring-orange-500/30 shadow-sm'
                          : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/60 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-900/60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Moon className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                          <span className="text-xs font-bold text-gray-900 dark:text-white">Dark Theme</span>
                        </div>
                      </div>
                      <p className="text-[10.5px] text-gray-500 dark:text-gray-400 leading-tight">
                        Sleek high-contrast dark card container
                      </p>
                      <div className="flex items-center gap-1.5 pt-1">
                        <div className="w-4 h-4 rounded-full border border-gray-700 bg-[#111827]" title="Card: #111827" />
                        <div className="w-4 h-4 rounded-full border border-gray-800 bg-[#0b0f19]" title="Canvas: #0b0f19" />
                        <div className="w-4 h-4 rounded-full border border-gray-300 bg-[#f3f4f6]" title="Text: #f3f4f6" />
                        <div className="w-4 h-4 rounded-full border border-gray-700 bg-[#1f2937]" title="Border: #1f2937" />
                      </div>
                    </button>
                  </div>
                </div>

                {/* 2. Custom Colors & Dimensions */}
                <div className="bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm space-y-3">
                  <div className="flex items-center justify-between pb-1.5 border-b border-gray-100 dark:border-gray-800/80">
                    <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Fine-Tune Colors & Dimensions
                    </span>
                    <span className="text-[10.5px] text-gray-500 dark:text-gray-400 font-mono">HEX / RGB</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5 text-xs">
                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Primary Accent Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentConfig.primary_color || '#ef4444'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, primary_color: e.target.value }))
                          }
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-700 bg-transparent cursor-pointer p-0 flex-none"
                        />
                        <input
                          type="text"
                          value={currentConfig.primary_color || '#ef4444'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, primary_color: e.target.value }))
                          }
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1 text-[11px] font-mono text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Header Text Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentConfig.header_text_color || currentConfig.primary_color || '#ef4444'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, header_text_color: e.target.value }))
                          }
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-700 bg-transparent cursor-pointer p-0 flex-none"
                        />
                        <input
                          type="text"
                          value={currentConfig.header_text_color || currentConfig.primary_color || '#ef4444'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, header_text_color: e.target.value }))
                          }
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1 text-[11px] font-mono text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Outer Canvas Background
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentConfig.background_color || '#f8fafc'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, background_color: e.target.value }))
                          }
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-700 bg-transparent cursor-pointer p-0 flex-none"
                        />
                        <input
                          type="text"
                          value={currentConfig.background_color || '#f8fafc'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, background_color: e.target.value }))
                          }
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1 text-[11px] font-mono text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Card / Body Background
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentConfig.card_background || '#ffffff'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, card_background: e.target.value }))
                          }
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-700 bg-transparent cursor-pointer p-0 flex-none"
                        />
                        <input
                          type="text"
                          value={currentConfig.card_background || '#ffffff'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, card_background: e.target.value }))
                          }
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1 text-[11px] font-mono text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Main Text Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentConfig.text_color || '#111827'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, text_color: e.target.value }))
                          }
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-700 bg-transparent cursor-pointer p-0 flex-none"
                        />
                        <input
                          type="text"
                          value={currentConfig.text_color || '#111827'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, text_color: e.target.value }))
                          }
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1 text-[11px] font-mono text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Muted / Secondary Text Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentConfig.muted_color || '#64748b'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, muted_color: e.target.value }))
                          }
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-700 bg-transparent cursor-pointer p-0 flex-none"
                        />
                        <input
                          type="text"
                          value={currentConfig.muted_color || '#64748b'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, muted_color: e.target.value }))
                          }
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1 text-[11px] font-mono text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Border Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentConfig.border_color || '#e2e8f0'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, border_color: e.target.value }))
                          }
                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-700 bg-transparent cursor-pointer p-0 flex-none"
                        />
                        <input
                          type="text"
                          value={currentConfig.border_color || '#e2e8f0'}
                          onChange={(e) =>
                            setCurrentConfig((prev) => ({ ...prev, border_color: e.target.value }))
                          }
                          className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-2.5 py-1 text-[11px] font-mono text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Container Max Width
                      </label>
                      <CustomSelect
                        options={MAX_WIDTH_OPTIONS}
                        value={currentConfig.max_width || '640px'}
                        onChange={(val: string) => setCurrentConfig((prev) => ({ ...prev, max_width: val }))}
                        placeholder="Select Max Width"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-[10.5px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Font Family
                      </label>
                      <CustomSelect
                        options={FONT_FAMILY_OPTIONS}
                        value={currentConfig.font_family || 'system-ui, -apple-system, sans-serif'}
                        onChange={(val: string) => setCurrentConfig((prev) => ({ ...prev, font_family: val }))}
                        placeholder="Select Font Family"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
              </div>
            </div>
          </div>
        )}

        {/* Right / Full: Live Email Preview Pane */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className={`flex-1 h-full p-4 overflow-hidden ${viewMode === 'preview' ? 'w-full' : ''}`}>
            <HtmlEmailPreview
              config={currentConfig}
              title="Preview"
            />
          </div>
        )}
      </div>

      {/* Edit Name & Description Modal */}
      {isEditMetaModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-md w-full max-w-md p-4 shadow-2xl space-y-3 animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Pencil className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                Edit Template Details
              </h3>
              <button
                type="button"
                onClick={() => setIsEditMetaModalOpen(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Template Name <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={editModalName}
                  onChange={(e) => setEditModalName(e.target.value)}
                  placeholder="e.g. Critical Port Alert"
                  className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveMetaModal();
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={editModalDesc}
                  onChange={(e) => setEditModalDesc(e.target.value)}
                  rows={3}
                  placeholder="Describe what triggers this template..."
                  className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500/60 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setIsEditMetaModalOpen(false)}
                className="px-3 py-1.5 rounded-md text-xs font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveMetaModal}
                className="px-4 py-1.5 rounded-md text-xs font-bold bg-orange-600 hover:bg-orange-500 dark:bg-orange-500 dark:hover:bg-orange-600 text-white shadow-md transition-colors cursor-pointer"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default HtmlTemplateEditor;
